/**
 * THE DEVICE PAGE'S DECISIONS, in one place and free of React (the asset
 * page redesign, October 2026, modelled on components/ticket/ticketView.js):
 * who is looking, which tabs they get, how a history line reads, how the
 * agent's report is summed up. The page (AssetLayout and its tabs) only
 * draws what these say.
 *
 * Never the protection: every write is the server's to allow.
 */
import {
  LIFECYCLE_LABEL, OPERATIONAL_LABEL, CONDITION_LABEL
} from '../../lib/assets';

export const FINISHED = ['closed', 'resolved', 'approved'];

export const FIELD_LABEL = {
  asset_tag: 'tag', serial_number: 'serial number', asset_category_id: 'category',
  manufacturer: 'make', model: 'model', model_number: 'model number', hostname: 'hostname',
  department_id: 'department', ownership: 'ownership', purchase_date: 'purchase date',
  supplier: 'supplier', invoice_ref: 'invoice', cost_ex_vat: 'cost', currency: 'currency',
  warranty_provider: 'warranty provider', warranty_ref: 'warranty reference',
  warranty_start: 'warranty start', warranty_end: 'warranty end',
  planned_refresh_date: 'replacement date', specs: 'specification', notes: 'description',
  is_shared: 'shared', lifecycle_status: 'status', operational_status: 'working state',
  condition: 'condition', ip_address: 'IP address', mac_address: 'MAC address', firmware: 'firmware',
  admin_url: 'admin page', parent_asset_id: 'device it is plugged into', company_id: 'company'
};

const statusWord = (field, v) => (field === 'operational_status' ? OPERATIONAL_LABEL[v]
  : field === 'condition' ? CONDITION_LABEL[v] : LIFECYCLE_LABEL[v]) || v;

/** One line of history, in a sentence. */
export function eventSentence(e) {
  switch (e.kind) {
    case 'created': return `Added to the register as ${e.to_value}`;
    case 'imported': return 'Imported';
    case 'assigned': return e.from_value
      ? `Given to ${e.to_value}, from ${e.from_value}` : `Given to ${e.to_value}`;
    case 'returned': return `Returned by ${e.from_value || 'its holder'}`;
    case 'moved': return e.from_value
      ? `Moved from ${e.from_value} to ${e.to_value || 'nowhere in particular'}`
      : `Placed at ${e.to_value}`;
    case 'status_changed':
    case 'condition_changed':
      return `${FIELD_LABEL[e.field] ? FIELD_LABEL[e.field][0].toUpperCase() + FIELD_LABEL[e.field].slice(1) : 'Status'}`
        + ` ${statusWord(e.field, e.from_value) || '—'} → ${statusWord(e.field, e.to_value)}`;
    case 'edited':
      return `Changed the ${FIELD_LABEL[e.field] || e.field} from ${e.from_value || 'nothing'} to ${e.to_value || 'nothing'}`;
    case 'noted': return 'Note';
    case 'repaired': return 'Repaired';
    case 'retired': return `Retired${e.to_value === 'disposed' ? ' and disposed of' : ''}`;
    case 'ticket_linked': return `Ticket ${e.ticket_number || e.to_value} is about this device`;
    case 'ticket_unlinked': return `Ticket ${e.ticket_number || e.from_value} no longer names this device`;
    case 'heartbeat_matched': return 'Reported in from the device';
    case 'discovered': return `${e.to_value || 'Discovered'}${e.reason ? ` — ${e.reason}` : ''}`;
    case 'pinned': return e.to_value ? `Placed on the floor plan of ${e.to_value}` : `Taken off the floor plan of ${e.from_value}`;
    case 'diagnostic_requested': return `Diagnostics asked for: ${e.to_value || ''}`;
    case 'lost': return 'Marked lost';
    case 'found': return 'Found';
    case 'enrolment_revoked': return 'Agent stopped';
    default: return e.kind;
  }
}

/**
 * THE HISTORY'S FILTERS. Each chip is a set of kinds; "Everything" is no
 * filter at all, so a kind nobody thought of still shows there.
 */
export const HISTORY_FILTERS = [
  { key: 'all', label: 'Everything' },
  { key: 'assignments', label: 'Assignments', kinds: ['assigned', 'returned'] },
  { key: 'moves', label: 'Moves', kinds: ['moved', 'pinned'] },
  { key: 'status', label: 'Status', kinds: ['status_changed', 'condition_changed', 'retired', 'edited', 'lost', 'found'] },
  { key: 'reports', label: 'Reports', kinds: ['heartbeat_matched', 'discovered', 'diagnostic_requested', 'enrolment_revoked', 'ticket_linked', 'ticket_unlinked'] },
  { key: 'notes', label: 'Notes', kinds: ['noted', 'repaired'] }
];

export const filterEvents = (events, key) => {
  const f = HISTORY_FILTERS.find((x) => x.key === key);
  if (!f || !f.kinds) return events;
  return events.filter((e) => f.kinds.includes(e.kind));
};

/** The number each chip would show. */
export const historyCounts = (events) => Object.fromEntries(
  HISTORY_FILTERS.map((f) => [f.key, filterEvents(events, f.key).length]));

/* ------------------------------------------------------------ the roles */

/**
 * 'none' | 'contractor' | 'read' | 'write' | 'admin'. The server already
 * said what this person has (`access`); this only names it for the tabs.
 */
export function roleOf({ data, meta, portal }) {
  if (!data?.access) return 'none';
  if (portal) return 'contractor';
  if (meta?.access?.admin) return 'admin';
  return data.access === 'write' ? 'write' : 'read';
}

/**
 * THE TABS. Sub-routes under /assets/:id, always shown for the role (an
 * empty tab shows a calm empty state rather than vanishing - Gordon). The
 * old address is Overview.
 */
export const TAB_DEFS = [
  { key: 'overview', path: '', label: 'Overview' },
  { key: 'history', path: 'history', label: 'History' },
  { key: 'tickets', path: 'tickets', label: 'Tickets' },
  { key: 'device', path: 'device', label: 'Device' },
  { key: 'notes', path: 'notes', label: 'Notes' },
  { key: 'files', path: 'files', label: 'Files' },
  { key: 'access', path: 'access', label: 'Access' }
];

export function tabsFor(role) {
  const keys = {
    contractor: ['overview', 'tickets', 'device', 'notes'],
    read: ['overview', 'history', 'tickets', 'device', 'notes', 'files'],
    write: ['overview', 'history', 'tickets', 'device', 'notes', 'files'],
    admin: ['overview', 'history', 'tickets', 'device', 'notes', 'files', 'access']
  }[role] || [];
  return TAB_DEFS.filter((t) => keys.includes(t.key));
}

/** The tab a path names: '/assets/x/history' -> 'history'. */
export function tabFromPath(pathname) {
  const last = String(pathname || '').replace(/\/+$/, '').split('/').pop();
  const hit = TAB_DEFS.find((t) => t.path && t.path === last);
  return hit ? hit.key : 'overview';
}

/* ----------------------------------------------------------- the agent */

export const ATTACHED_WORD = { monitor: 'Screen', dock: 'Dock', printer: 'Printer', storage: 'Drive', keyboard: 'Keyboard',
  mouse: 'Mouse', webcam: 'Webcam', audio: 'Audio', hub: 'Hub', other: 'Other' };

export const ago = (when, now = Date.now()) => {
  if (!when) return 'never';
  const mins = Math.round((now - new Date(when).getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 36) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const stateWord = (s) => ({ on: 'On', off: 'Off' }[s] || 'Not known');
export const stateTone = (s, goodIsOn = true) => (s === 'on' ? (goodIsOn ? 'success' : 'warning')
  : s === 'off' ? (goodIsOn ? 'critical' : 'success') : 'idle');

/**
 * THE REPORTING DOT in the header. An enrolled agent is reporting, or not
 * reporting for N days (the firm's offline window, default 14); a device
 * without an agent says nothing at all.
 */
export function reporting(asset, device, now = Date.now()) {
  const e = device?.enrolment;
  if (!e) return null;
  const last = asset?.last_heartbeat_at ? new Date(asset.last_heartbeat_at).getTime() : null;
  const silentDays = last === null ? null : (now - last) / 86400000;
  const quiet = silentDays === null || silentDays > (device.offline_days || 14);
  if (!quiet) return { tone: 'success', text: `Reporting · seen ${ago(asset.last_heartbeat_at, now)}` };
  if (silentDays === null) return { tone: 'warning', text: 'Not reporting · never reported in' };
  const days = Math.floor(silentDays);
  return { tone: 'warning', text: `Not reporting for ${days} day${days === 1 ? '' : 's'}` };
}

/**
 * WHO IS SIGNED IN AGAINST WHO HOLDS IT. The agent's report names the
 * account at the machine (facts.user_id when Jarvis knows them); the
 * register names a holder. When they differ - a person who is not the
 * holder - the page offers to make them the holder. A contractor holder
 * (093) has no account to compare, so that case only says so.
 */
export function signedInDiffers(asset, meta) {
  const f = asset?.facts;
  if (!f?.user_id) return null;
  if (asset.assigned_user_id && f.user_id === asset.assigned_user_id) return null;
  const name = (meta?.users || []).find((u) => u.id === f.user_id)?.display_name || f.user_upn || f.user_account;
  return {
    userId: f.user_id, name,
    sentence: asset.assigned_user_id ? 'not who the register says has it'
      : asset.holder_kind === 'contractor' ? 'the register has it with a contractor' : 'the register says nobody has it'
  };
}

/** The sentence after "Signed in", as the Device tab's facts and the Overview card say it. */
export const signedInSuffix = (asset) => {
  const f = asset?.facts;
  if (!f?.user_id) return '';
  if (asset.assigned_user_id && f.user_id !== asset.assigned_user_id) return ' — not who the register says has it';
  if (!asset.assigned_user_id && asset.holder_kind === 'contractor') return ' — the register has it with a contractor';
  return '';
};
